import React, {useEffect, useState} from 'react';
import styles from './multiple-plot-list.module.scss'
import backIcon from '../../../images/arrow-left-purple.svg'
import { useTranslation } from 'react-i18next';
import {useAppSelector} from "../../../hooks/useAppSelector";
import CardMultiplePlotList from "./card-multiple-plot-list/card-multiple-plot-list";
import ModalMultiplePlotList from "./modal-multiple-plot-list/modal-multiple-plot-list";
import {useNavigate} from "react-router-dom";
import {LayersDataTypes} from "../../../types/layers-data-types";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { intersect, planarLength } from "@arcgis/core/geometry/geometryEngine";
import {Layer} from "../../../interfaces/layer";
import {LandPlot} from "../../../interfaces/card";
import {useAppDispatch} from "../../../hooks/useAppDispatch";
import {
    deletePlotToMerge,
    setAdjacentPlot
} from "../../../services/reducers/multiple-plot";
import CardAdjacentPlot from "./card-adjacent-plot/card-adjacent-plot";
import {setPlotColors} from "../../../services/actions/map";
import CustomScrollbar from "../../ui-kit/scrollbar/scrollbar";
import Geometry from "@arcgis/core/geometry/Geometry";
import Graphic from "@arcgis/core/Graphic";
import {checkConnectivity, countSharedEdges} from "../../../utils/helpers-for-find-adjacent-plots";
import { LayerType } from '../../../types/layer-type';
import {getFid} from "../../../utils/plot-utils";

const MultiplePlotList = () => {
    const {adjacentPlots, plotsToMerge, initialPlot, sourceUrl, filledPlots, finalMultiplePlot} = useAppSelector(state => state.multiplePlotList);
    const layers = useAppSelector(state => state.map.layers);
    const landPlotsData = useAppSelector(state => state.filters.plots.data);

    const [clearSelectionModalVisible, setClearSelectionModalVisible] = useState<boolean>(false)

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!initialPlot) {
            window.location.href = `${process.env.PUBLIC_URL}/search`;
        }
    }, [initialPlot]);

    useEffect(() => {
        if (!initialPlot?.geometry && plotsToMerge.length === 0) return;

        const findAdjacentPlots = async (layers: Layer[], referencePlots: LandPlot[], landPlotsData: LandPlot[]) => {
            const landPlotLayers = layers
                .filter(layer => layer.dataType === LayersDataTypes.Plots && layer.type === LayerType.ArcgisFeatureServer)
                .map(layer => new FeatureLayer({ url: layer.url, definitionExpression: layer.style2D?.query }));
            let allAdjacentPlots: LandPlot[] = [];

            const processLayerForPlot = async (featureLayer: any, refPlot: any) => {
                const query = featureLayer.createQuery();
                query.outFields = ["*"];
                query.geometry = refPlot.geometry;
                let results = [];

                query.spatialRelationship = "intersects";
                const resultsIntersects = await featureLayer.queryFeatures(query);
                const filteredIntersectPlots = resultsIntersects.features.filter((feature: Graphic) => {
                    const intersection = intersect(refPlot.geometry, feature.geometry);
                    return intersection && planarLength(intersection as Geometry, "meters") > 1;
                });
                const matchingIntersectPlots = landPlotsData.filter(plot =>
                    filteredIntersectPlots.some((feature: Graphic) => String(getFid(feature.attributes)) === String(plot.fid)) &&
                    String(plot.fid) !== String(initialPlot?.fid)
                );
                if (matchingIntersectPlots.length) results.push(...matchingIntersectPlots);

                query.spatialRelationship = "touches";
                const resultsTouches = await featureLayer.queryFeatures(query);
                if (resultsTouches.features.length) {
                    const adjacentPlotsFids = resultsTouches.features.map((f: Graphic) => String(getFid(f.attributes)));
                    let filteredTouchesPlots = landPlotsData.filter(plot =>
                        adjacentPlotsFids.includes(String(plot.fid)) &&
                        !referencePlots.some(ref => String(ref.fid) === String(plot.fid))
                    );
                    filteredTouchesPlots = countSharedEdges(refPlot, filteredTouchesPlots).filter(plot => plot.sharedEdges > 0);
                    if (filteredTouchesPlots.length) results.push(...filteredTouchesPlots);
                }
                return results;
            };

            const promises = landPlotLayers.flatMap(featureLayer =>
                referencePlots.map(refPlot => processLayerForPlot(featureLayer, refPlot))
            );
            const resultsArray = await Promise.all(promises);
            resultsArray.forEach(results => allAdjacentPlots.push(...results));

            const uniqueAdjacentPlots = Array.from(new Map(allAdjacentPlots.map(plot => [plot.fid, plot])).values());
            const filteredAdjacentPlots = uniqueAdjacentPlots.filter(plot =>
                !plotsToMerge.some(mergedPlot => mergedPlot.fid === plot.fid)
            );
            dispatch(setAdjacentPlot(filteredAdjacentPlots));
        };

        findAdjacentPlots(layers, [initialPlot!, ...plotsToMerge], landPlotsData);
    }, [initialPlot, plotsToMerge, layers, landPlotsData, dispatch]);

    useEffect(() => {
        if (!initialPlot || plotsToMerge.length === 0) return;

        const stillConnected = checkConnectivity(plotsToMerge, initialPlot);
        const disconnectedPlots = plotsToMerge.filter(p => !stillConnected.some(sc => sc.fid === p.fid));
        if (disconnectedPlots.length) {
            disconnectedPlots.forEach(plot => dispatch(deletePlotToMerge(plot)));
        }
    }, [plotsToMerge, initialPlot, dispatch, adjacentPlots]);

    useEffect(() => {
        if (!initialPlot || adjacentPlots.length || plotsToMerge.length || filledPlots.length) {
            dispatch(setPlotColors());
        }
    }, [initialPlot, adjacentPlots, plotsToMerge, filledPlots, dispatch]);


    const { t } = useTranslation();
    
    const handleClose = () => {
        if (sourceUrl) {
            navigate(sourceUrl);
        }
    };

    if (!initialPlot) return null;

    return (
        <>
            <div className={styles.multiplePlotList}>
                <CustomScrollbar marginRight='13px'>
                    <div className={styles.scrollContent}>
                        <div className={styles.header}>
                            <button onClick={handleClose} className={styles.backBtn}>
                                <img src={backIcon} alt={t('multiplePlotList.backButtonAlt')}/>
                                {t('multiplePlotList.title')}
                            </button>
                            <button onClick={() => setClearSelectionModalVisible(true)} className={styles.clearBtn}>
                                {t('multiplePlotList.clearSelection')}
                                <span></span>
                            </button>
                        </div>
                        <p className={styles.description}>{t('multiplePlotList.description')}</p>
                        {finalMultiplePlot && (
                            <CardMultiplePlotList data={finalMultiplePlot}/>
                        )}
                        {plotsToMerge?.length > 0 && (
                            <div className={styles.plotsToMerge}>
                                {plotsToMerge.map((plot) => (
                                        <CardAdjacentPlot key={plot.fid} type='deleteToMerge' data={plot}/>
                                    )
                                )}
                            </div>
                        )}
                        <div className={styles.adjacentPlots}>
                            <div className={styles.title}>
                                <span>{t('multiplePlotList.plotsCanBeSelected')}</span>
                                <p>{t('multiplePlotList.availablePlots', {count: adjacentPlots.length})}</p>
                            </div>
                            {adjacentPlots?.length > 0 && (
                                adjacentPlots.map(adjacentPlot =>
                                    <CardAdjacentPlot key={adjacentPlot.fid} type='addToMerge' data={adjacentPlot}/>
                                )
                            )}
                        </div>
                    </div>
                </CustomScrollbar>
            </div>
            {clearSelectionModalVisible && (
                <ModalMultiplePlotList onClose={() => setClearSelectionModalVisible(false)} type='Clear'/>
            )}
        </>
    );
};

export default MultiplePlotList;
